import React from "react";
import PropTypes from "prop-types";
import {Badge, Col, OverlayTrigger, Row, Spinner, Tooltip} from "react-bootstrap";
import Modals from "../../../../components/modals";
import {formatDate, formatDueDate} from "../../../../utils/util";

const componentName = "changeStatus";

const changeStatusOptions = [
  { value: "LEFT_MESSAGE", label: "Left Message" },
  { value: "CHANGED_PCPS", label: "Changed PCP" },
  { value: "UNABLE_TO_CONTACT", label: "Unable to Contact" },
  { value: "PATIENT_REFUSAL", label: "Patient Refused" },
];

const changeStatusLabels = changeStatusOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const changeStatusLabelOverrides = {
  APPOINTMENT_SCHEDULED: "Appointment Scheduled",
  APPOINTMENT_UNSCHEDULED: "No Show",
  APPOINTMENT_COMPLETED: "Appointment Completed",
  APPOINTMENT_CONFIRMED: "Appointment Confirmed",
  APPOINTMENT_CANCELLED: "Appointment Cancelled",
};

const getStatusDisplayLabel = (statusEnum) => {
  if (!statusEnum) return null;
  return changeStatusLabels[statusEnum] || changeStatusLabelOverrides[statusEnum] || statusEnum;
};

const resolveVisitAppointmentDate = (visit) => {
  if (!visit) return null;
  return (
    visit.appointmentDate ||
    visit.patientGapAppointmentDate ||
    visit.visitDate ||
    visit.appointmentDateTime ||
    null
  );
};

const resolveVisitStatusDate = (currentVisit) => {
  if (!currentVisit?.statusEnum || currentVisit.statusEnum === "OPEN") return null;
  if (currentVisit.statusEnum === "APPOINTMENT_SCHEDULED") {
    return currentVisit.statusDate || resolveVisitAppointmentDate(currentVisit) || null;
  }
  if (currentVisit.statusEnum === "APPOINTMENT_UNSCHEDULED") {
    if (currentVisit.appointmentCancelReason !== "NO_SHOW") {
      return null;
    }
    return currentVisit.appointmentCancelDate || currentVisit.statusDate || null;
  }
  return currentVisit.statusDate || null;
};

const getStatusBadgeText = (currentVisit, statusEnum) => {
  if (!statusEnum) return null;
  if (statusEnum === "APPOINTMENT_UNSCHEDULED" && currentVisit?.appointmentCancelReason !== "NO_SHOW") {
    return null;
  }
  const label = getStatusDisplayLabel(statusEnum);
  const dateValue = resolveVisitStatusDate(currentVisit);
  if (!dateValue) return label;
  const formattedDate = formatDate(dateValue);
  return formattedDate ? `${label} on ${formattedDate}` : label;
};

const renderVisitGroup = (params) => {
  const {
    label,
    visits,
    groupKey,
    idPrefix,
    visitGapSelections,
    updateVisitGapSelection,
  } = params;

  if (!visits?.length) {
    return null;
  }

  return (
    <Row className="mb-3">
      <Col>
        <span className="fw-bold d-block">{label}</span>
        <div>
          {visits.map((visit) => {
            const careGapId = visit?.careGapId;
            if (!careGapId) return null;

            const postpartumPrefix = "Follow-Up after Child Birth";
            const visitDescription = visit.description || "";
            const isPostpartumVisit = visitDescription.startsWith(postpartumPrefix);
            const selection = visitGapSelections[careGapId] || { careGapId, selected: false, statusEnum: "" };
            const visitStatus = visit.statusEnum && visit.statusEnum !== "OPEN" ? visit.statusEnum : null;
            const checkboxId = `${idPrefix}-${groupKey}-${careGapId}`;
            const selectId = `${checkboxId}-status`;
            const statusBadgeText = getStatusBadgeText(visit, visitStatus);
            const { text: dueText, icon: dueIcon } = formatDueDate(visit?.nextVisitDays);
            const normalizedDueText = dueText ? dueText.trim().toLowerCase() : "";
            const showDueBadge =
              !!normalizedDueText && normalizedDueText !== "today" && !normalizedDueText.endsWith("ago");

            return (
              <div className="d-flex flex-row justify-content-between align-items-start mb-2" key={careGapId}>
                <div className="form-check">
                  <input
                    id={checkboxId}
                    className="form-check-input"
                    type="checkbox"
                    name={checkboxId}
                    checked={!!selection.selected}
                    onChange={(event) =>
                      updateVisitGapSelection(careGapId, {
                        selected: event.target.checked,
                      })
                    }
                  />
                  <div className="d-flex flex-column justify-content-start align-items-start">
                    <label className="form-check-label" htmlFor={checkboxId}>
                      {isPostpartumVisit ? (
                        <OverlayTrigger
                          placement="auto"
                          overlay={(
                            <Tooltip id={`${componentName}-postpartumTooltip-${careGapId}`}>
                              Sensitive context. The data indicates that this patient has recently given birth. The details that follow may be incomplete, and outcomes may include loss, adoption, or other outcomes. As you reach out to assist with follow-up care, please proceed with respect, sensitivity, and empathy.
                            </Tooltip>
                          )}
                        >
                          <span>{visitDescription}</span>
                        </OverlayTrigger>
                      ) : (
                        visitDescription
                      )}
                    </label>
                    <div className="d-flex flex-row align-items-center gap-2 mt-1">
                      {showDueBadge && (
                        <Badge
                          pill
                          bg="transparent"
                          id={`${idPrefix}-nextVisit-${groupKey}-${careGapId}`}
                          className="border border-secondary text-dark d-inline-flex align-items-center"
                        >
                          <i className={`fa-regular ${dueIcon || "fa-calendar"} me-1`}></i> {dueText}
                        </Badge>
                      )}
                      {statusBadgeText && (
                        <Badge
                          pill
                          bg="transparent"
                          id={`${idPrefix}-statusBadge-${groupKey}-${careGapId}`}
                          className="border border-danger text-danger"
                        >
                          {statusBadgeText}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <select
                  className="form-select w-25"
                  id={selectId}
                  value={selection.statusEnum || ""}
                  onChange={(event) =>
                    updateVisitGapSelection(careGapId, { statusEnum: event.target.value })
                  }
                  disabled={!selection.selected}
                >
                  <option value="">Select status</option>
                  {changeStatusOptions.map((option) => (
                    <option key={option.value} value={option.value} disabled={visitStatus === option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </Col>
    </Row>
  );
};

const ChangeStatusModal = ({
  show,
  onHide,
  onSubmit,
  patient,
  visitGaps,
  visitGapSelections,
  visitGapsLoading,
  visitGapsError,
  changeStatusFormError,
  visitGapsSubmitting,
  updateVisitGapSelection,
  size = "lg",
}) => {
  const idPrefix = `${componentName}-changeStatus`;
  const visitsNow = visitGaps?.dueNowVisits || [];
  const visitsSoon = visitGaps?.dueSoonVisits || [];
  const hasVisits = visitsNow.length > 0 || visitsSoon.length > 0;

  return (
    <Modals
      centered
      show={show}
      onHide={onHide}
      size={size}
      title="Change Status"
      titleid={`${componentName}-ChangeStatusModal`}
      body={
        <div>
          <Row className="mb-3">
            <Col>
              <span id={`${idPrefix}-patientNameLabel`} className="fw-bold d-block">
                Patient Name
              </span>
              <span id={`${idPrefix}-patientNameValue`} className="forced-capital d-block">
                {patient?.fullName}
              </span>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <p>
                Please select one or more visits and the appropriate status (Left Message, Changed PCP,
                Unable to Contact, or Patient Refused).
              </p>
            </Col>
          </Row>

          {visitGapsError && (
            <Row className="mb-3">
              <Col>
                <div className="text-danger">{visitGapsError}</div>
              </Col>
            </Row>
          )}

          {changeStatusFormError && (
            <Row className="mb-3">
              <Col>
                <div className="text-danger">{changeStatusFormError}</div>
              </Col>
            </Row>
          )}

          {visitGapsLoading ? (
            <div className="text-center py-4">
              <output aria-live="polite">
                <Spinner animation="border" />
                <span className="visually-hidden">Loading...</span>
              </output>
            </div>
          ) : (
            <>
              {renderVisitGroup({
                label: "Recommended Now",
                visits: visitsNow,
                groupKey: "now",
                idPrefix,
                visitGapSelections,
                updateVisitGapSelection,
              })}
              {renderVisitGroup({
                label: "Recommended Soon",
                visits: visitsSoon,
                groupKey: "soon",
                idPrefix,
                visitGapSelections,
                updateVisitGapSelection,
              })}
              {!visitsNow.length && !visitsSoon.length && (
                <div className="text-muted fst-italic small">No visits found.</div>
              )}
            </>
          )}

          {visitGapsSubmitting && (
            <Row className="mt-2">
              <Col>
                <small className="text-muted">Submitting updates...</small>
              </Col>
            </Row>
          )}
        </div>
      }
      completeBtn="Submit"
      completeBtnId={`${componentName}-ChangeStatusSubmit`}
      disabledClickButton={!hasVisits}
      closeBtn="Cancel"
      closeBtnId={`${componentName}-ChangeStatusCancel`}
      onCreate={onSubmit}
    />
  );
};

ChangeStatusModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  patient: PropTypes.object,
  visitGaps: PropTypes.shape({
    dueNowVisits: PropTypes.arrayOf(PropTypes.object),
    dueSoonVisits: PropTypes.arrayOf(PropTypes.object),
  }),
  visitGapSelections: PropTypes.object,
  visitGapsLoading: PropTypes.bool,
  visitGapsError: PropTypes.string,
  changeStatusFormError: PropTypes.string,
  visitGapsSubmitting: PropTypes.bool,
  updateVisitGapSelection: PropTypes.func.isRequired,
  size: PropTypes.string,
};

ChangeStatusModal.defaultProps = {
  patient: null,
  visitGaps: {
    dueNowVisits: [],
    dueSoonVisits: [],
  },
  visitGapSelections: {},
  visitGapsLoading: false,
  visitGapsError: null,
  changeStatusFormError: null,
  visitGapsSubmitting: false,
  size: "lg",
};

export default ChangeStatusModal;
